import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from '../../styles/Sidebar.module.scss';
import { getCategoryUrl } from '../../utils/urls';
import { getCategoryAncestorIds } from '../../utils/categoryTree';

const findInTree = (nodes, id) => {
  for (const node of nodes) {
    if (Number(node.id) === id) {
      return node;
    }
    const found = findInTree(node.children || [], id);
    if (found) {
      return found;
    }
  }
  return null;
};

const SidebarCategoryMenu = ({ tree = [], categories = [] }) => {
  const location = useLocation();
  const activeCategoryId = useMemo(() => {
    const match = location.pathname.match(/^\/categories\/(\d+)/);
    return match ? Number(match[1]) : null;
  }, [location.pathname]);

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!activeCategoryId) {
      return;
    }
    const ancestors = getCategoryAncestorIds(categories, activeCategoryId);
    const next = new Set(ancestors);
    const activeNode = findInTree(tree, activeCategoryId);
    if (activeNode?.children?.length) {
      next.add(activeCategoryId);
    }
    setExpandedIds(next);
  }, [activeCategoryId, categories, tree]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (!tree.length) {
    return <p className={styles.catalogEmpty}>Категорії з товарами зʼявляться тут</p>;
  }

  const renderSubNodes = (nodes, depth = 0) => nodes.map((node) => {
    const hasNested = (node.children || []).length > 0;

    return (
      <li key={node.id} className={styles.catalogSubItem}>
        <NavLink
          to={getCategoryUrl(node)}
          title={node.name}
          className={({ isActive }) =>
            `${styles.catalogSubLink} ${isActive ? styles.catalogLinkActive : ''}`
          }
          style={{ '--depth': depth }}
        >
          <span className={styles.catalogSubLinkText}>{node.name}</span>
        </NavLink>
        {hasNested && (
          <ul className={styles.catalogSubmenuNested}>
            {renderSubNodes(node.children, depth + 1)}
          </ul>
        )}
      </li>
    );
  });

  const renderNode = (node) => {
    const hasChildren = (node.children || []).length > 0;
    const isExpanded = expandedIds.has(node.id);

    if (!hasChildren) {
      return (
        <li key={node.id} className={styles.catalogItem}>
          <NavLink
            to={getCategoryUrl(node)}
            title={node.name}
            className={({ isActive }) =>
              `${styles.catalogLink} ${isActive ? styles.catalogLinkActive : ''}`
            }
          >
            <span className={styles.catalogLinkText}>{node.name}</span>
          </NavLink>
        </li>
      );
    }

    return (
      <li key={node.id} className={`${styles.catalogItem} ${isExpanded ? styles.catalogItemOpen : ''}`}>
        <div className={styles.catalogGroupHead}>
          <NavLink
            to={getCategoryUrl(node)}
            title={node.name}
            className={({ isActive }) =>
              `${styles.catalogLink} ${styles.catalogParentLink} ${isActive ? styles.catalogLinkActive : ''}`
            }
          >
            <span className={styles.catalogLinkText}>{node.name}</span>
          </NavLink>
          <button
            type="button"
            className={`${styles.catalogToggle} ${isExpanded ? styles.catalogToggleOpen : ''}`}
            onClick={() => toggleExpanded(node.id)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Згорнути підкатегорії' : 'Розгорнути підкатегорії'}
          />
        </div>
        <ul className={`${styles.catalogSubmenu} ${isExpanded ? styles.catalogSubmenuOpen : ''}`}>
          {renderSubNodes(node.children || [])}
        </ul>
      </li>
    );
  };

  return (
    <nav className={styles.catalogNav} aria-label="Каталог товарів">
      <div className={styles.catalogScrollWrap}>
        <ul className={styles.catalogList}>
          {tree.map(renderNode)}
        </ul>
      </div>
    </nav>
  );
};

export default SidebarCategoryMenu;
